import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import {
  NoDataContainer,
  pageSize,
  paginationLabel,
  TablePaginationStyle,
} from "../../Common/styles/table";
import { ExpandMore } from "@mui/icons-material";
import { errorToastMessage } from "../../../utils/toast";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { useParams } from "react-router-dom";
import { JournalAccordion } from "./FaqStyles";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";

const FaqPreview = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<any>([]);
  const [dataCount, setDataCount] = useState<number>(0);
  const [parentExpanded, setParentExpanded] = useState<string | false>(false);
  const [childExpanded, setChildExpanded] = useState<string | false>(false);
  const [page, setPage] = useState(0);
  const { studySlug } = useParams();
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let url = `/faq/slug/${studySlug}?page=${page + 1}&size=${pageSize}`;
        if (searchText) {
          url += `&search=${searchText}`;
        }
        const res: AxiosResponse = await http.get(url);
        const apiData = res.data?.data;

        const newData = apiData?.rows
          ?.map((item: any) => ({
            id: item?.id,
            name: item?.name,
            position: item?.position,
            faqs: (item?.faqs || []).sort(
              (a: any, b: any) => a.position - b.position
            ),
            children: item?.children || [],
          }))
          .sort((a: any, b: any) => a.position - b.position);
        setData(newData || []);
        setDataCount(apiData?.count);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoading, setData, studySlug, page, searchText]);

  const handleParentChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setParentExpanded(isExpanded ? panel : false);
    };

  const handleChildChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setChildExpanded(isExpanded ? panel : false);
    };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const modifySearchTerm = useMemo(
    () =>
      debounce((val) => {
        setPage(0);
        setSearchText(val);
      }, 500),
    [setPage, setSearchText]
  );

  return (
    <Box sx={{ p: 2.5, height: "100%", overflow: "auto" }}>
      <Typography variant="h6" mb={3}>
        Frequently Asked Questions
      </Typography>
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderRadius: "8px 8px 0 0",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search for FAQs"
          style={{ width: "100%" }}
          sx={{
            input: {
              fontSize: "16px",
            },
          }}
          defaultValue={searchText}
          onChange={(e) => modifySearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress size={25} />
          </Box>
        ) : data.length > 0 ? (
          <Box>
            {data.map((item: any) => {
              return (
                <Accordion
                  key={item.id}
                  expanded={parentExpanded === `panel${item.id}`}
                  onChange={handleParentChange(`panel${item.id}`)}
                  sx={JournalAccordion}
                >
                  <AccordionSummary
                    expandIcon={
                      <IconButton>
                        <ExpandMore sx={{ fontSize: 24 }} />
                      </IconButton>
                    }
                    sx={{
                      ".Mui-expanded": {
                        m: "12px 0px !important",
                      },
                    }}
                  >
                    <Box py={1.5}>
                      <Typography
                        fontWeight={600}
                        fontSize={"18px"}
                        color={"primary.main"}
                      >
                        {item?.name}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    {item?.faqs?.length > 0 ? (
                      <>
                        {item?.faqs.map((faq: any, index: number) => {
                          return (
                            <Accordion
                              key={faq.id}
                              expanded={childExpanded === `panel${faq.id}`}
                              onChange={handleChildChange(`panel${faq.id}`)}
                              sx={{
                                boxShadow: "none",
                                borderBottom:
                                  index === item?.faqs.length - 1
                                    ? "none"
                                    : "1px solid #E5E7EB",
                                "::before": {
                                  backgroundColor: "transparent",
                                },
                              }}
                            >
                              <AccordionSummary
                                expandIcon={
                                  <IconButton>
                                    <ExpandMore sx={{ fontSize: 24 }} />
                                  </IconButton>
                                }
                                sx={{
                                  ".Mui-expanded": {
                                    m: "12px 0px !important",
                                  },
                                }}
                              >
                                <Box py={1.5}>
                                  <Typography
                                    fontWeight={500}
                                    fontSize={"16px"}
                                    color={"primary.main"}
                                  >
                                    {faq?.question}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ padding: 1 }}>
                                <div className="tiptap-text" key={faq?.id}>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: faq?.answer,
                                    }}
                                  ></div>
                                </div>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </>
                    ) : (
                      <NoDataContainer>
                        <Typography variant="body1" color="gray">
                          No Data
                        </Typography>
                      </NoDataContainer>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
            {dataCount > pageSize && (
              <TablePagination
                sx={TablePaginationStyle}
                count={dataCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[pageSize]}
                labelDisplayedRows={paginationLabel}
              />
            )}
          </Box>
        ) : (
          <NoDataContainer
            sx={{
              height: "100%",
              borderRadius: "12px",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium" color="#4B5563">
              No FAQ Available
            </Typography>
          </NoDataContainer>
        )}
      </Box>
    </Box>
  );
};

export default FaqPreview;
